"use client";

import { useMemo } from "react";
import { motion } from "framer-motion";
import { useStore } from "@/lib/store";
import { formatDate } from "@/lib/utils";

export function CyclesView() {
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const allTeams = useStore((s) => s.teams);
  const allCycles = useStore((s) => s.cycles);
  const issues = useStore((s) => s.issues);
  const teams = useMemo(
    () => allTeams.filter((t) => t.workspaceId === activeWorkspaceId),
    [allTeams, activeWorkspaceId]
  );
  const cycles = useMemo(() => {
    const ids = new Set(teams.map((t) => t.id));
    return allCycles.filter((c) => ids.has(c.teamId));
  }, [allCycles, teams]);

  const rows = useMemo(() => {
    return cycles.map((c) => {
      const cIssues = issues.filter((i) => i.cycleId === c.id);
      const done = cIssues.filter((i) => i.status === "done").length;
      const progress = cIssues.length ? Math.round((done / cIssues.length) * 100) : 0;
      const now = Date.now();
      const started = now >= c.startDate;
      const ended = now > c.endDate;
      const active = started && !ended;
      const team = teams.find((t) => t.id === c.teamId);
      return { c, team, progress, total: cIssues.length, done, active, ended };
    });
  }, [cycles, issues, teams]);

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      <div className="max-w-5xl mx-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {rows.map(({ c, team, progress, total, done, active, ended }, i) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04 }}
            className="rounded-xl border border-[var(--border)] bg-[var(--bg-secondary)] p-5 hover:border-[var(--border-strong)] transition-colors"
          >
            <div className="flex items-center gap-2 mb-3">
              <span
                className="w-5 h-5 rounded-[4px] flex items-center justify-center text-[11px]"
                style={{ background: `${team?.color}22`, color: team?.color }}
              >
                {team?.icon}
              </span>
              <div className="text-[13.5px] font-semibold">{c.name}</div>
              {active && (
                <span className="ml-auto text-[10.5px] font-medium px-1.5 py-0.5 rounded-full bg-[var(--green)]/15 text-[var(--green)]">
                  Active
                </span>
              )}
              {ended && (
                <span className="ml-auto text-[10.5px] font-medium px-1.5 py-0.5 rounded-full bg-[var(--bg-tertiary)] text-[var(--fg-muted)]">
                  Completed
                </span>
              )}
            </div>
            <div className="text-[11.5px] text-[var(--fg-tertiary)] mb-4">
              {formatDate(c.startDate)} – {formatDate(c.endDate)}
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="h-1.5 flex-1 rounded-full bg-[var(--bg-tertiary)] overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.6 }}
                  className="h-full rounded-full bg-[var(--accent)]"
                />
              </div>
              <span className="text-[11px] text-[var(--fg-tertiary)]">{progress}%</span>
            </div>
            <div className="text-[11.5px] text-[var(--fg-tertiary)]">
              {done} of {total} tasks completed
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
